<template>
    <div class="ms-page">
        <!-- ─────────────────── HEADER ─────────────────── -->
        <header class="ms-header">
            <div class="ms-header__inner">
                <div class="ms-header__title-wrap">
                    <h1 class="ms-title">כל המנויים במערכת</h1>
                    <span class="ms-total-pill" :title="`סה״כ ${totalItems} מנויים`">
                        <v-icon size="15">mdi-sim</v-icon>
                        <span>{{ totalItems.toLocaleString('he-IL') }}</span>
                    </span>
                </div>

                <div class="ms-header__actions">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <button v-on="on" type="button" class="ms-export-btn" @click="exportManuimToExcel"
                                aria-label="ייצוא לאקסל">
                                <v-icon size="16" color="white">mdi-file-excel-outline</v-icon>
                                <span>ייצוא לאקסל</span>
                            </button>
                        </template>
                        <span>ייצוא כל המנויים המסוננים לאקסל</span>
                    </v-tooltip>
                </div>
            </div>
        </header>

        <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

        <!-- ─────────────────── MAIN ─────────────────── -->
        <main class="ms-main">
            <!-- STATUS SUMMARY (clickable filters) -->
            <section class="ms-summary-grid" aria-label="סינון לפי סטטוס">
                <button type="button" class="ms-summary-card ms-summary-card--all"
                    :class="{ 'ms-summary-card--selected': filterType === 'all' }" @click="setFilter('all')">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-sim</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">סה״כ מנויים</span>
                        <strong class="ms-summary-card__value">{{ counts.all.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <button type="button" class="ms-summary-card ms-summary-card--active"
                    :class="{ 'ms-summary-card--selected': filterType === 'active' }" @click="setFilter('active')">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-check-circle-outline</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">פעילים</span>
                        <strong class="ms-summary-card__value">{{ counts.active.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <button type="button" class="ms-summary-card ms-summary-card--process"
                    :class="{ 'ms-summary-card--selected': filterType === 'proccess' }" @click="setFilter('proccess')">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-clock-outline</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">בתהליך</span>
                        <strong class="ms-summary-card__value">{{ counts.proccess.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <button type="button" class="ms-summary-card ms-summary-card--inactive"
                    :class="{ 'ms-summary-card--selected': filterType === 'not_active' }"
                    @click="setFilter('not_active')">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-cancel</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">לא פעילים</span>
                        <strong class="ms-summary-card__value">{{ counts.not_active.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>
            </section>

            <!-- TOOLBAR -->
            <div class="ms-toolbar">
                <div class="ms-search">
                    <v-icon class="ms-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="ms-search__input"
                        placeholder="חיפוש לפי לקוח, ת״ז, מספר מנוי, סים, מפעיל או חבילה…" @input="handleSearchInput" />
                    <button v-if="search" type="button" class="ms-search__clear"
                        @click="search = ''; handleSearchInput()" aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>

                <div class="ms-toolbar__hint">
                    <v-icon size="16">mdi-information-outline</v-icon>
                    <span>ניתן לבצע פעולות מהירות ישירות מתוך הטבלה</span>
                </div>

                <div class="ms-pagesize">
                    <label for="ms-pagesize-select">שורות</label>
                    <select id="ms-pagesize-select" class="ms-pagesize__select" v-model.number="itemsPerPage"
                        @change="onItemsPerPageChange">
                        <option v-for="n in [20, 50, 100, 500, 1000]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- TABLE -->
            <div class="ms-table-card">
                <v-data-table id="table1" :headers="headers" :items="manuim" hide-default-footer
                    :server-items-length="totalItems" :items-per-page="itemsPerPage" :page.sync="currentPage"
                    :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @update:page="onPageChange"
                    @update:sort-by="onSortChange" @update:sort-desc="onSortChange" item-key="_id" class="ms-table">

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="ms-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.index="{ index }">
                        <span class="ms-index">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</span>
                    </template>

                    <!-- CUSTOMER cell - the new column showing which customer this manui belongs to -->
                    <template v-slot:item.customer_id="{ item }">
                        <div class="ms-customer-cell">
                            <div class="ms-customer-cell__body">
                                <span class="ms-customer-cell__name">
                                    {{ getCustomerName(item) }}
                                </span>
                                <span class="ms-customer-cell__cardid"
                                    v-if="item.customer_id && item.customer_id.card_id">
                                    {{ item.customer_id.card_id }}
                                </span>
                            </div>
                            <v-tooltip bottom v-if="item.customer_id && item.customer_id._id">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="goToCustomerManuim(item)" aria-label="פתיחת דף מנויי הלקוח">
                                        <v-icon size="15">mdi-open-in-new</v-icon>
                                    </button>
                                </template>
                                <span>מעבר לדף מנויי הלקוח</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <!-- 1. נמחק בפועל -->
                        <v-tooltip bottom v-if="item.status === 'not_active'" transition="scale-transition"
                            open-delay="150" close-delay="100" color="#2E2E2E" max-width="260"
                            content-class="custom-tooltip">
                            <template v-slot:activator="{ on }">
                                <span v-on="on" class="ms-status-pill ms-status-pill--inactive cursor-pointer">
                                    <span class="ms-status-pill__dot"></span>
                                    לא פעיל
                                </span>
                            </template>
                            <div class="tooltip-content">
                                <div class="tooltip-title">המנוי בוטל</div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#fdb721" class="ml-1">mdi-calendar</v-icon>
                                    נמחק בתאריך {{ formatDateString(item.deletedAt) }}
                                </div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#8066f0" class="ml-1">mdi-account</v-icon>
                                    על ידי {{ item.deletedBy && item.deletedBy.name || 'לא ידוע' }}
                                </div>
                            </div>
                        </v-tooltip>

                        <!-- 2. פעיל, אך קיים תאריך מחיקה מתוכנן -->
                        <v-menu v-else-if="isPendingCancellation(item)" open-on-hover offset-y bottom open-delay="150"
                            close-delay="220" nudge-bottom="4" min-width="250" transition="scale-transition"
                            content-class="ms-pending-menu">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="{ ...on, click: on.mouseenter }"
                                    class="ms-status-pill ms-status-pill--active ms-status-pill--pending cursor-pointer">
                                    <span class="ms-status-pill__dot"></span>
                                    פעיל
                                    <span
                                        :class="['ms-pending-badge', isDeletionOverdue(item) && 'ms-pending-badge--overdue']">
                                        <v-icon class="ms-pending-badge__icon">
                                            {{ isDeletionOverdue(item) ? 'mdi-alert-outline' : 'mdi-clock-alert-outline'
                                            }}
                                        </v-icon>
                                        {{ pendingLabel(item) }}
                                    </span>
                                </span>
                            </template>

                            <div class="tooltip-content">
                                <div v-if="isDeletionOverdue(item)" class="tooltip-title tooltip-title--danger">
                                    <v-icon x-small color="#ff6b6b" class="ml-1">mdi-alert-circle-outline</v-icon>
                                    תאריך המחיקה עבר
                                </div>
                                <div v-else class="tooltip-title tooltip-title--warn">
                                    <v-icon x-small color="#ffb01f" class="ml-1">mdi-progress-clock</v-icon>
                                    מתוכנן למחיקה
                                </div>

                                <div class="tooltip-line">
                                    <v-icon x-small color="#fdb721" class="ml-1">mdi-calendar-clock</v-icon>
                                    תאריך מחיקה: {{ formatDateOnly(deletionDate(item)) }}
                                </div>
                                <div class="tooltip-line">
                                    <v-icon x-small color="#8066f0" class="ml-1">mdi-account</v-icon>
                                    בוטל על ידי {{ item.deletedBy && item.deletedBy.name || 'לא ידוע' }}
                                </div>
                                <div class="tooltip-note">
                                    {{ isDeletionOverdue(item)
                                        ? 'המנוי עדיין פעיל למרות שתאריך המחיקה חלף — נדרשת בדיקה'
                                        : 'המנוי יישאר פעיל עד למועד זה' }}
                                </div>

                                <button v-if="hasPermission('delete_manui')" type="button" class="ms-tt-btn"
                                    @click="openCancelDeleteDialog(item)">
                                    <v-icon size="15" class="ml-1" color="#8ce99a">mdi-delete-off-outline</v-icon>
                                    ביטול תהליך המחיקה
                                </button>
                            </div>
                        </v-menu>

                        <!-- 3. פעיל רגיל / בתהליך -->
                        <span v-else
                            :class="['ms-status-pill', item.status === 'active' ? 'ms-status-pill--active' : 'ms-status-pill--process']">
                            <span class="ms-status-pill__dot"></span>
                            {{ item.status === 'active' ? 'פעיל' : 'בתהליך' }}
                        </span>
                    </template>

                    <template v-slot:item.manui_number="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-number">{{ item.manui_number || '—' }}</span>
                            <v-tooltip bottom
                                v-if="item.status === 'proccess' && item.manui_number && hasPermission('update_manui_number')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditPhoneDialog(item)" aria-label="עדכון מספר מנוי">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עדכון מספר מנוי</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.plan_id.operating_company="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-company-badge">{{ companiesList[item.plan_id.operating_company] }}</span>
                            <v-tooltip bottom
                                v-if="item.status !== 'proccess' && item.manui_number && hasPermission('update_company')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditcompanyDialog(item)" aria-label="החלפת מפעיל">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>החלפת מפעיל</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.sim_number="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-number">{{ item.sim_number || '—' }}</span>
                            <v-tooltip bottom
                                v-if="hasPermission('update_sim_number') && (item.status === 'proccess' || !item.sim_number)">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn" @click="openEditSimDialog(item)"
                                        aria-label="עדכון מספר סים">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>{{ item.status === 'proccess' ? 'עדכון מספר סים' : 'השלמת מספר סים' }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.plan_id.name="{ item }">
                        <div class="ms-inline-cell">
                            <span class="ms-plan-name">{{ item.plan_id.name }}</span>
                            <v-tooltip bottom v-if="item.status === 'active'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click="openEditPlanDialog(item)" aria-label="עדכון חבילה">
                                        <v-icon size="15">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עדכון חבילה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.actions="{ item, index }">
                        <div class="ms-actions">
                            <v-tooltip bottom
                                v-if="item.status === 'active' && item.manui_number && hasPermission('edit_manui')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--callblock"
                                        @click="openCallBlockDialog(item)" aria-label="חסימת חיוג">
                                        <v-icon size="17">mdi-phone-lock</v-icon>
                                    </button>
                                </template>
                                <span>חסימת חיוג</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="hasOperatorActions(item) && hasPermission('operator_actions')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--operator"
                                        @click="openOperatorActionsDialog(item)" aria-label="פעולות מול ספק">
                                        <v-icon size="17">mdi-cellphone-cog</v-icon>
                                    </button>
                                </template>
                                <span>פעולות מול ספק סלולר</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="hasPermission('edit_manui') && item.status !== 'not_active'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--edit"
                                        @click="openEditDialog(item, index)" aria-label="עריכת מנוי">
                                        <v-icon size="17">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עריכה</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="role === 'admin'">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--customer"
                                        @click="openEditCustomerDialog(item, index)" aria-label="שינוי לקוח">
                                        <v-icon size="17">mdi-account-switch-outline</v-icon>
                                    </button>
                                </template>
                                <span>שינוי לקוח</span>
                            </v-tooltip>

                            <!-- <v-tooltip bottom v-if="role !== 'agent'">
                                <template v-slot:activator="{ on }">
                                    <v-badge :content="item.ticketsCount" :value="item.ticketsCount > 0" color="#0d2c6d"
                                        overlap offset-x="10" offset-y="8">
                                        <button v-on="on" type="button" class="ms-action-btn ms-action-btn--ticket"
                                            @click="createTicket(item, index)" aria-label="פתיחת פנייה">
                                            <v-icon size="17">mdi-email-fast-outline</v-icon>
                                        </button>
                                    </v-badge>
                                </template>
                                <span>פתח פנייה למנוי זה</span>
                            </v-tooltip> -->

                            <v-tooltip bottom v-if="item.status !== 'active' && hasPermission('delete_manui')">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--delete"
                                        @click="openDeleteDialog(item, index)" aria-label="מחיקת מנוי">
                                        <v-icon size="17">mdi-delete-outline</v-icon>
                                    </button>
                                </template>
                                <span>מחיקה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="ms-empty">
                            <v-progress-linear v-if="progressShow" indeterminate color="#fdb721" height="3"
                                class="ms-empty__progress"></v-progress-linear>
                            <template v-else>
                                <div class="ms-empty__icon"><v-icon size="30">mdi-sim-off</v-icon></div>
                                <h3 class="ms-empty__title">לא נמצאו מנויים</h3>
                                <p class="ms-empty__hint">נסה לשנות את הסינון או החיפוש.</p>
                            </template>
                        </div>
                    </template>

                    <template v-slot:no-results>
                        <div class="ms-empty">
                            <div class="ms-empty__icon"><v-icon size="30">mdi-magnify-close</v-icon></div>
                            <h3 class="ms-empty__title">לא נמצאו תוצאות</h3>
                            <p class="ms-empty__hint">נסה לשנות את החיפוש או הסינון.</p>
                        </div>
                    </template>
                </v-data-table>

                <div v-if="totalItems > itemsPerPage" class="ms-pagination-wrap">
                    <v-pagination v-model="currentPage" circle :length="Math.ceil(totalItems / itemsPerPage)"
                        :total-visible="7" @input="onPageChange"></v-pagination>
                </div>
            </div>
        </main>

        <v-overlay :value="overlay">
            <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
        </v-overlay>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

        <EditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            :customerId="currentCustomerId" @itemEdited="editedItemSaved" />

        <EditPlanDialog v-model="editPlanDialog" v-if="editPlanDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditSimDialog v-model="editSimDialog" v-if="editSimDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditCustomerDialog v-model="EditCustomerDialog" v-if="EditCustomerDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <EditPhoneDialog v-model="editPhoneDialog" v-if="editPhoneDialog" :formTitle="formTitle"
            :itemToEdit="editedItem" @itemEdited="editedItemSaved" />

        <EditCompanyDialog v-model="editCompanyDialog" v-if="editCompanyDialog" :formTitle="formTitle"
            :itemToEdit="editedItem" @itemEdited="editedItemSaved" />

        <CreateTicketDialog v-model="sendMessageDialog" v-if="sendMessageDialog" :messageItem="messageItem"
            :customerId="currentCustomerId" />

        <AlertDialog v-model="alertDialog" v-if="alertDialog" :item="itemToDelete" @itemDeleted="itemDeleted" />

        <OperatorActionsDialog v-model="operatorActionsDialog" v-if="operatorActionsDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <CallBlockDialog v-model="callBlockDialog" v-if="callBlockDialog" :itemToEdit="editedItem"
            @itemEdited="editedItemSaved" />

        <CancelDeleteDialog v-model="cancelDeleteDialog" v-if="cancelDeleteDialog" :item="itemToCancelDelete"
            @deleteCanceled="deleteCanceled" />
    </div>
</template>

<script>

import SnackBar from '@/components/widgets/snackBar.vue'
import ApiServices from '@/services/api.service'
import Utils from "@/util/utils";
import Auth from '@/services/auth.service';
import EditDialog from '@/components/manuim/dialogs/EditDialog'
import EditPlanDialog from '@/components/manuim/dialogs/EditPlanDialog'
import EditSimDialog from '@/components/manuim/dialogs/EditSimDialog'
import EditPhoneDialog from '@/components/manuim/dialogs/EditPhoneDialog'
import EditCompanyDialog from '@/components/manuim/dialogs/EditCompanyDialog'
import EditCustomerDialog from '@/components/manuim/dialogs/EditCustomer.vue'
import CreateTicketDialog from '@/components/manuim/dialogs/CreateTicketDialog'
import ProgressShow from '@/components/widgets/progressShow.vue'
import AlertDialog from '@/components/manuim/dialogs/AlertDialog'
import OperatorActionsDialog from '@/components/manuim/dialogs/OperatorActionsDialog'
import CallBlockDialog from '@/components/manuim/dialogs/CallBlockDialog'
import CancelDeleteDialog from '@/components/manuim/dialogs/CancelDeleteDialog'
import _ from 'lodash';

export default {
    components: {
        SnackBar,
        ProgressShow,
        EditDialog,
        EditCustomerDialog,
        CreateTicketDialog,
        AlertDialog,
        EditPlanDialog,
        EditSimDialog,
        EditPhoneDialog,
        EditCompanyDialog,
        OperatorActionsDialog,
        CallBlockDialog,
        CancelDeleteDialog
    },
    data: () => ({
        headers: [
            { text: '#', value: 'index', sortable: false },
            { text: 'לקוח', value: 'customer_id', sortable: false },
            { text: 'מנוי', value: 'manui_number' },
            { text: 'מפעיל', value: 'plan_id.operating_company' },
            { text: 'מספר סים', value: 'sim_number' },
            { text: 'סוג', value: 'type' },
            { text: 'חבילה', value: 'plan_id.name' },
            { text: 'סטטוס', value: 'status' },
            { text: 'רמת סינון', value: 'community_level' },
            { text: 'משווק', value: 'seller_id.name' },
            { text: 'נוצר בתאריך', value: 'createdAt' },
            { text: 'עודכן בתאריך', value: 'updatedAt' },
            { text: 'פעולות', value: 'actions', sortable: false },
        ],
        overlay: false,
        manuim: [],
        totalItems: 0,
        counts: {
            all: 0,
            active: 0,
            proccess: 0,
            not_active: 0,
        },
        currentPage: 1,
        itemsPerPage: 20,
        sortBy: "createdAt",
        sortDesc: true,
        search: "",
        filterType: "all",
        progressShow: false,

        // dialogs state
        addEditDialog: false,
        editPlanDialog: false,
        EditCustomerDialog: false,
        editPhoneDialog: false,
        editSimDialog: false,
        editCompanyDialog: false,
        sendMessageDialog: false,
        operatorActionsDialog: false,
        callBlockDialog: false,
        alertDialog: false,

        formTitle: "",
        editedItem: {},
        messageItem: {},
        itemToDelete: {},
        currentCustomerId: "", // set from item.customer_id._id when opening dialogs

        // snackbar
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",

        role: Auth.getUserRole(),
        companiesList: {
            "clickCallMobile": "קליקול מובייל",
            "hotMobile": "הוט מובייל",
            "partner": "פרטנר"
        },
        cancelDeleteDialog: false,
        itemToCancelDelete: {},
    }),
    methods: {
        async getAllManuim(resetPage = false) {
            if (resetPage) {
                this.currentPage = 1;
            }
            try {
                this.progressShow = true;
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + `/manuim/get_all_manuim`;
                const query = new URLSearchParams({
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy || 'createdAt',
                    sortDesc: this.sortDesc ? 'desc' : 'asc',
                    search: (this.search || '').replace(/[\u200E\u200F\u202A-\u202E]/g, '').trim(),
                    filterType: this.filterType
                }).toString();

                const res = await fetch(`${api}?${query}`, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "red");
                } else if (res.status === 200) {
                    this.manuim = jsonObject.data || [];
                    this.totalItems = jsonObject.totalItems || 0;
                    if (jsonObject.counts) {
                        this.counts = {
                            all: jsonObject.counts.all || 0,
                            active: jsonObject.counts.active || 0,
                            proccess: jsonObject.counts.proccess || 0,
                            not_active: jsonObject.counts.not_active || 0,
                        };
                    }
                    this.formatDetails();
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },
        formatDetails() {
            this.manuim.forEach(manui => {
                manui.createdAt = Utils.formatDateString(manui.createdAt);
                manui.updatedAt = Utils.formatDateString(manui.updatedAt);
            });
        },
        formatDateString(date) {
            return Utils.formatDateString(date);
        },
        isPendingCancellation(item) {
            return item && item.status === 'active' && !!item.deletedAt;
        },
        deletionDate(item) {
            if (!item || !item.deletedAt) return null;
            const d = new Date(item.deletedAt);
            return isNaN(d.getTime()) ? null : d;
        },
        // הפרש בימי לוח: 0 = היום, 1 = מחר, שלילי = התאריך עבר
        daysUntilDeletion(item) {
            const target = this.deletionDate(item);
            if (!target) return null;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const t = new Date(target);
            t.setHours(0, 0, 0, 0);
            return Math.round((t.getTime() - today.getTime()) / 86400000);
        },
        isDeletionOverdue(item) {
            const days = this.daysUntilDeletion(item);
            return days !== null && days < 0;
        },
        pendingLabel(item) {
            const days = this.daysUntilDeletion(item);
            if (days === null) return 'בתהליך מחיקה';
            if (days < 0) return `באיחור ${Math.abs(days)} ימים`;
            if (days === 0) return 'נמחק היום';
            if (days === 1) return 'מחר';
            return `בעוד ${days} ימים`;
        },
        formatDateOnly(date) {
            if (!date) return '—';
            const d = new Date(date);
            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('he-IL');
        },
        // ── Filter / search / paging handlers ──
        setFilter(type) {
            if (this.filterType === type) return;
            this.filterType = type;
            this.getAllManuim(true);
        },
        handleSearchInput() {
            this.debouncedGetAllManuim(true);
        },
        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getAllManuim();
        },
        onItemsPerPageChange() {
            this.currentPage = 1;
            this.getAllManuim();
        },
        onSortChange() {
            this.getAllManuim();
        },
        // ── Customer cell helpers ──
        getCustomerName(item) {
            if (!item.customer_id) return '—';
            const name = item.customer_id.name || '';
            const family = item.customer_id.family || '';
            const full = `${name} ${family}`.trim();
            return full || '—';
        },
        goToCustomerManuim(item) {
            if (!item.customer_id || !item.customer_id._id) return;
            const cust = item.customer_id;
            const routeData = this.$router.resolve({
                name: "manuim",
                params: {
                    id: cust._id,
                    name: `${cust.name || ''} ${cust.family || ''}`.trim() || '—',
                    cardId: cust.card_id || '—'
                }
            });
            window.open(routeData.href, '_blank');
        },
        // ── Snackbar ──
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },
        // ── Dialog openers (populate currentCustomerId from the item) ──
        setCurrentCustomerId(item) {
            this.currentCustomerId = (item && item.customer_id && item.customer_id._id) || "";
        },
        openEditDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.formTitle = "ערוך פרטי מנוי";
            this.addEditDialog = true;
        },
        openEditCustomerDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.EditCustomerDialog = true;
        },
        openEditPlanDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.formTitle = "עדכון חבילה למנוי";
            this.editPlanDialog = true;
        },
        openEditPhoneDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.formTitle = "עדכון מספר מנוי";
            this.editPhoneDialog = true;
        },
        openEditcompanyDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.formTitle = "החלפת מפעיל";
            this.editCompanyDialog = true;
        },
        openEditSimDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.formTitle = "עדכון מספר סים";
            this.editSimDialog = true;
        },
        openDeleteDialog(item) {
            this.setCurrentCustomerId(item);
            this.itemToDelete = item;
            this.formTitle = "האם אתה בטוח שברצונך למחוק את המנוי?";
            this.alertDialog = true;
        },
        createTicket(item) {
            this.setCurrentCustomerId(item);
            this.messageItem = item;
            this.sendMessageDialog = true;
        },
        openOperatorActionsDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.operatorActionsDialog = true;
        },
        openCallBlockDialog(item) {
            this.setCurrentCustomerId(item);
            this.editedItem = item;
            this.callBlockDialog = true;
        },
        // ── Save / delete callbacks ──
        editedItemSaved(item, text, color) {
            this.showSnackBar(text, color);
            if (item === "") return;
            const index = this.manuim.findIndex(m => m._id === item._id);
            if (index !== -1) {
                Object.assign(this.manuim[index], item);
            }
        },
        itemDeleted(item, text, color) {
            if (!item) {
                this.showSnackBar(text, color);
                return;
            }
            const index = this.manuim.findIndex(m => m._id === item._id);
            if (index !== -1) {
                this.manuim.splice(index, 1);
                this.totalItems = Math.max(0, this.totalItems - 1);
                if (this.counts.all > 0) this.counts.all--;
                if (item.status && typeof this.counts[item.status] === 'number' && this.counts[item.status] > 0) {
                    this.counts[item.status]--;
                }
            }
            this.showSnackBar(text, color);
        },
        // ── Permissions ──
        hasOperatorActions(item) {
            const operator = item && item.plan_id && item.plan_id.operating_company;
            const supportedOperators = ['partner', 'hotMobile', 'clickCallMobile'];
            return supportedOperators.includes(operator) && item.status === 'active';
        },
        hasPermission(action) {
            return this.role === 'admin' || Auth.getUserPermissions().includes(action);
        },
        // ── Export to Excel ──
        async exportManuimToExcel() {
            try {
                this.progressShow = true;
                const token = localStorage.getItem("token");
                const query = new URLSearchParams({
                    search: (this.search || '').replace(/[\u200E\u200F\u202A-\u202E]/g, '').trim(),
                    filterType: this.filterType
                }).toString();
                const api = process.env.VUE_APP_BASE_URL + `/manuim/export_all_manuim_to_excel?${query}`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                this.progressShow = false;
                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בייצוא הקובץ", "red");
                    return;
                }
                const blob = await res.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = `all_manuim_${this.filterType}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(downloadUrl);
                this.showSnackBar("הקובץ יורד בהצלחה", "green");
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("שגיאה בייצוא: " + error, "red");
            }
        },
        openCancelDeleteDialog(item) {
            console.log(item);
            this.itemToCancelDelete = item;
            this.cancelDeleteDialog = true;
        },
        deleteCanceled(item, updated, text, color) {
            const index = this.manuim.findIndex(m => m._id === item._id);
            if (index !== -1) {
                this.$set(this.manuim, index, Object.assign({}, this.manuim[index], updated));
            }
            this.showSnackBar(text, color);
        },
    },
    created() {
        this.debouncedGetAllManuim = _.debounce(this.getAllManuim, 700);
        this.getAllManuim();
    },
}
</script>

<style scoped>
.ms-page {
    --m-navy: #0d2c6d;
    --m-navy-soft: #142f78;
    --m-indigo: #3F51B5;
    --m-indigo-soft: rgba(63, 81, 181, 0.10);
    --m-purple: #8066f0;
    --m-purple-soft: rgba(128, 102, 240, 0.10);
    --m-gold: #fdb721;
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-surface: #ffffff;
    --m-bg: #f3f6f9;
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --m-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --m-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--m-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────── HEADER ─────────────── */
.ms-header {
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--m-shadow-md);
}

.ms-header__inner {
    max-width: 1700px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.ms-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.ms-header__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ms-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.ms-total-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}

.ms-total-pill ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
}

.ms-export-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(253, 183, 33, 0.18);
    border: 1px solid rgba(253, 183, 33, 0.35);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s var(--m-ease);
}

.ms-export-btn:hover {
    background: rgba(253, 183, 33, 0.32);
    border-color: rgba(253, 183, 33, 0.55);
}

/* ─────────────── MAIN + SUMMARY ─────────────── */
.ms-main {
    max-width: 1700px;
    margin: 22px auto 0;
    padding: 0 22px;
}

.ms-summary-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.ms-summary-card {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 72px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--m-surface);
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-md);
    text-align: right;
    cursor: pointer;
    transition: all 0.2s var(--m-ease);
    font-family: inherit;
}

.ms-summary-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--m-shadow-lg);
}

.ms-summary-card--selected {
    border-color: var(--m-navy);
    background: linear-gradient(to bottom, rgba(13, 44, 109, 0.04), var(--m-surface));
    box-shadow: 0 0 0 2px rgba(13, 44, 109, 0.25), var(--m-shadow-md);
}

.ms-summary-card__icon {
    width: 39px;
    height: 39px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 11px;
}

.ms-summary-card--all .ms-summary-card__icon {
    color: var(--m-navy);
    background: rgba(13, 44, 109, 0.10);
}

.ms-summary-card--active .ms-summary-card__icon {
    color: var(--m-green);
    background: var(--m-green-soft);
}

.ms-summary-card--process .ms-summary-card__icon {
    color: var(--m-orange);
    background: var(--m-orange-soft);
}

.ms-summary-card--inactive .ms-summary-card__icon {
    color: var(--m-red);
    background: var(--m-red-soft);
}

.ms-summary-card__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.ms-summary-card__label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--m-text-soft);
}

.ms-summary-card__value {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--m-text);
    font-feature-settings: "tnum" 1;
}

/* ─────────────── TOOLBAR ─────────────── */
.ms-toolbar {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: center;
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--m-surface);
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-sm);
    margin-bottom: 12px;
}

.ms-search {
    position: relative;
    display: flex;
    align-items: center;
}

.ms-search__icon {
    position: absolute;
    right: 10px;
    color: var(--m-text-mute);
}

.ms-search__input {
    width: 100%;
    padding: 8px 36px 8px 36px;
    border-radius: 10px;
    border: 1px solid var(--m-border);
    background: #fff;
    color: var(--m-text);
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s var(--m-ease), box-shadow 0.15s var(--m-ease);
}

.ms-search__input:focus {
    border-color: var(--m-indigo);
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.15);
}

.ms-search__clear {
    position: absolute;
    left: 8px;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: transparent;
    color: var(--m-text-mute);
    cursor: pointer;
    border: none;
}

.ms-search__clear:hover {
    background: var(--m-border-soft);
    color: var(--m-text);
}

.ms-toolbar__hint {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--m-text-soft);
    font-size: 0.82rem;
}

.ms-pagesize {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--m-text-soft);
    font-size: 0.85rem;
}

.ms-pagesize__select {
    padding: 5px 8px;
    border-radius: 8px;
    border: 1px solid var(--m-border);
    background: #fff;
    color: var(--m-text);
    outline: none;
    font-family: inherit;
}

/* ─────────────── TABLE ─────────────── */
.ms-table-card {
    background: var(--m-surface);
    border-radius: 14px;
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-md);
    overflow: hidden;
}

::v-deep .ms-table .v-data-table__wrapper {
    border-radius: 14px;
}

::v-deep .ms-table thead tr {
    background: linear-gradient(to bottom, #fafcff, #f3f6fa);
}

::v-deep .ms-table thead th {
    border-bottom: 1px solid var(--m-border) !important;
    color: var(--m-text-soft) !important;
    font-weight: 700 !important;
    font-size: 0.79rem !important;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

::v-deep .ms-table tbody td {
    border-bottom: 1px solid var(--m-border-soft) !important;
    padding: 10px 12px !important;
    color: var(--m-text);
    font-size: 0.88rem;
    vertical-align: middle;
}

::v-deep .ms-table tbody tr:hover {
    background: rgba(63, 81, 181, 0.04) !important;
}

.ms-th {
    display: inline-block;
    color: var(--m-text-soft);
    font-weight: 700;
    font-size: 0.79rem;
    letter-spacing: 0.02em;
}

.ms-index {
    display: inline-grid;
    place-items: center;
    min-width: 24px;
    padding: 2px 6px;
    border-radius: 6px;
    background: rgba(13, 44, 109, 0.08);
    color: var(--m-navy);
    font-weight: 700;
    font-size: 0.78rem;
    font-feature-settings: "tnum" 1;
}

/* ─────────────── CUSTOMER CELL (new column) ─────────────── */
.ms-customer-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 220px;
}

.ms-customer-cell__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.ms-customer-cell__name {
    font-weight: 700;
    color: var(--m-text);
    font-size: 0.88rem;
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
}

.ms-customer-cell__cardid {
    color: var(--m-text-mute);
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.1;
    direction: ltr;
    font-feature-settings: "tnum" 1;
}

/* ─────────────── INLINE CELLS ─────────────── */
.ms-inline-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.ms-number {
    font-feature-settings: "tnum" 1;
    direction: ltr;
    color: var(--m-text);
    font-weight: 600;
}

.ms-company-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-plan-name {
    color: var(--m-text);
    font-weight: 600;
}

.ms-mini-btn {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: transparent;
    color: var(--m-text-mute);
    border: none;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-mini-btn:hover {
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
}

/* ─────────────── STATUS PILL ─────────────── */
.ms-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
}

.ms-status-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.ms-status-pill--active {
    background: var(--m-green-soft);
    color: var(--m-green);
}

.ms-status-pill--process {
    background: var(--m-orange-soft);
    color: var(--m-orange);
}

.ms-status-pill--inactive {
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-status-pill--process .ms-status-pill__dot {
    animation: ms-pulse 1.8s ease-in-out infinite;
}

@keyframes ms-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 3px rgba(192, 105, 28, 0.18);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(192, 105, 28, 0.04);
    }
}

/* ─────────────── ACTIONS ─────────────── */
.ms-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
}

.ms-action-btn {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: transparent;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-action-btn--edit:hover {
    background: var(--m-blue);
    color: #fff;
    border-color: var(--m-blue);
    box-shadow: 0 5px 12px -4px rgba(25, 118, 210, 0.55);
}

.ms-action-btn--customer:hover {
    background: var(--m-indigo);
    color: #fff;
    border-color: var(--m-indigo);
    box-shadow: 0 5px 12px -4px rgba(63, 81, 181, 0.60);
}

.ms-action-btn--ticket:hover {
    background: var(--m-purple);
    color: #fff;
    border-color: var(--m-purple);
    box-shadow: 0 5px 12px -4px rgba(128, 102, 240, 0.60);
}

.ms-action-btn--delete:hover {
    background: var(--m-red);
    color: #fff;
    border-color: var(--m-red);
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.60);
}

.ms-action-btn--operator:hover {
    background: var(--m-gold);
    color: #fff;
    border-color: var(--m-gold);
    box-shadow: 0 5px 12px -4px rgba(253, 183, 33, 0.55);
}

.ms-action-btn--callblock:hover {
    background: var(--m-orange);
    color: #fff;
    border-color: var(--m-orange);
    box-shadow: 0 5px 12px -4px rgba(192, 105, 28, 0.55);
}

/* ─────────────── EMPTY + PAGINATION ─────────────── */
.ms-empty {
    padding: 50px 20px;
    text-align: center;
}

.ms-empty__progress {
    max-width: 320px;
    margin: 0 auto;
}

.ms-empty__icon {
    width: 62px;
    height: 62px;
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    border-radius: 50%;
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
}

.ms-empty__title {
    margin: 0 0 4px;
    color: var(--m-text);
    font-size: 1.02rem;
}

.ms-empty__hint {
    margin: 0;
    color: var(--m-text-mute);
    font-size: 0.86rem;
}

.ms-pagination-wrap {
    display: flex;
    justify-content: center;
    padding: 17px 12px 12px;
    border-top: 1px solid var(--m-border-soft);
}

::v-deep .ms-pagination-wrap .v-pagination__item,
::v-deep .ms-pagination-wrap .v-pagination__navigation {
    border: 1px solid var(--m-border) !important;
    box-shadow: none !important;
}

::v-deep .ms-pagination-wrap .v-pagination__item--active {
    border-color: var(--m-navy) !important;
    background: var(--m-navy) !important;
    color: #fff !important;
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.35) !important;
}

/* ─────────────── TOOLTIP ─────────────── */
.custom-tooltip {
    padding: 10px 12px !important;
    border-radius: 10px;
    color: #fff !important;
    background-color: #2e2e2e !important;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
    direction: rtl;
    font-size: 13px;
    line-height: 1.5;
}

.tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tooltip-title {
    margin-bottom: 4px;
    color: #fdb721;
    font-size: 14px;
    font-weight: bold;
}

.tooltip-line {
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.cursor-pointer {
    cursor: pointer;
}

/* ─────────────── RESPONSIVE ─────────────── */
@media (max-width: 1050px) {
    .ms-toolbar {
        grid-template-columns: 1fr auto;
    }

    .ms-toolbar__hint {
        display: none;
    }
}

@media (max-width: 760px) {
    .ms-header__inner {
        align-items: stretch;
        flex-direction: column;
        padding: 0 18px;
        gap: 10px;
    }

    .ms-summary-grid {
        grid-template-columns: repeat(2, minmax(130px, 1fr));
    }

    .ms-main {
        margin-top: 16px;
        padding: 0 12px;
    }

    .ms-toolbar {
        grid-template-columns: 1fr;
    }

    .ms-pagesize {
        justify-content: flex-end;
    }
}

/* ── פעיל עם מחיקה מתוכננת ── */
.ms-status-pill--pending {
    padding-inline-end: 4px;
    box-shadow: inset 0 0 0 1px rgba(192, 105, 28, 0.3);
}

.ms-pending-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-inline-start: 5px;
    padding: 2px 7px;
    border-radius: 999px;
    background: var(--m-orange-soft);
    color: var(--m-orange);
    font-size: 0.68rem;
    font-weight: 800;
    line-height: 1.4;
    white-space: nowrap;
    animation: ms-pending-glow 2.4s ease-in-out infinite;
}

.ms-pending-badge__icon {
    font-size: 13px !important;
    color: var(--m-orange) !important;
}

.ms-pending-badge--overdue {
    background: var(--m-red-soft);
    color: var(--m-red);
    animation: ms-overdue-glow 1.6s ease-in-out infinite;
}

.ms-pending-badge--overdue .ms-pending-badge__icon {
    color: var(--m-red) !important;
}

@keyframes ms-pending-glow {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(192, 105, 28, 0.3);
    }

    50% {
        box-shadow: 0 0 0 4px rgba(192, 105, 28, 0);
    }
}

@keyframes ms-overdue-glow {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(211, 47, 47, 0);
    }
}

@media (prefers-reduced-motion: reduce) {

    .ms-pending-badge,
    .ms-pending-badge--overdue {
        animation: none;
    }
}

.tooltip-title--warn {
    display: flex;
    align-items: center;
    color: #ffb01f;
}

.tooltip-title--danger {
    display: flex;
    align-items: center;
    color: #ff6b6b;
}

.tooltip-note {
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
}

.ms-tt-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 9px;
    padding: 7px 10px;
    border: 1px solid rgba(140, 233, 154, 0.35);
    border-radius: 8px;
    background: rgba(140, 233, 154, 0.12);
    color: #8ce99a;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
}

.ms-tt-btn:hover {
    background: #8ce99a;
    border-color: #8ce99a;
    color: #14351b;
}

.ms-tt-btn:hover .v-icon {
    color: #14351b !important;
}
</style>

<style>
.ms-pending-menu {
    padding: 11px 13px;
    border-radius: 10px;
    background-color: #2e2e2e !important;
    color: #fff;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35) !important;
    direction: rtl;
    font-size: 13px;
    line-height: 1.5;
    contain: none !important;
}
</style>